import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    GroupTrialPersonnelCreateBreadcrumbs,
    GroupTrialPersonnelDetailsBreadcrumbs,
    TrialGroupPersonnelCreateBreadcrumbs,
    TrialGroupPersonnelDetailsBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupActions from "../../../actions/trialGroup";
import * as TrialPersonnelActions from "../../../actions/trialPersonnel";
import { NotificationAlert } from "../../../components/common/index";
import {
    DatePickerFormInputFabric,
    SelectFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { createPersonnelSearchLoader } from "../../../functions/personnel";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import {
    GroupTrialPersonnelSubMenu,
    GroupTrialSubMenu,
    TrialGroupPersonnelSubMenu,
    TrialGroupSubMenu,
} from "../../../helpers/subMenuHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./TrialGroupPersonnelPage.scss";

interface TrialGroupPersonnelPageProps {
    form: Dtos.TrialPersonnel & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trialPersonnel: Dtos.TrialPersonnel;
    trialGroup: Dtos.TrialGroup;
    lookups: Dtos.Lookup[];
    loadingTrialPersonnel: boolean;
    loadTrialPersonnelSuccess: boolean;
    loadTrialPersonnelFailure: boolean;
    loadTrialPersonnelErrorCode: string;
    savingTrialPersonnel: boolean;
    saveTrialPersonnelSuccess: boolean;
    saveTrialPersonnelFailure: boolean;
    creatingTrialPersonnel: boolean;
    createTrialPersonnelSuccess: boolean;
    validatingTrialPersonnel: boolean;
    loadingTrialGroup: boolean;
    loadTrialGroupFailure: boolean;
    loadTrialGroupErrorCode: string;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialPersonnel: TrialPersonnelActions.ITrialPersonnelClearActionCreator;
    loadTrialPersonnel: TrialPersonnelActions.ITrialPersonnelLoadByIdActionCreator;
    saveTrialPersonnel: TrialPersonnelActions.ITrialPersonnelSaveActionCreator;
    validateTrialPersonnel: TrialPersonnelActions.ITrialPersonnelValidateActionCreator;
    createTrialPersonnel: TrialPersonnelActions.ITrialPersonnelCreateActionCreator;
    clearTrialGroup: TrialGroupActions.ITrialGroupClearActionCreator;
    loadTrialGroup: TrialGroupActions.ITrialGroupLoadByIdActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialPersonnel";

const TrialGroupPersonnelPage: FC<TrialGroupPersonnelPageProps> = ({
    form,
    formState,
    formProperties,
    trialPersonnel,
    trialGroup,
    lookups,
    loadingTrialPersonnel,
    loadTrialPersonnelSuccess,
    loadTrialPersonnelFailure,
    loadTrialPersonnelErrorCode,
    savingTrialPersonnel,
    saveTrialPersonnelSuccess,
    saveTrialPersonnelFailure,
    creatingTrialPersonnel,
    createTrialPersonnelSuccess,
    validatingTrialPersonnel,
    loadingTrialGroup,
    loadTrialGroupFailure,
    loadTrialGroupErrorCode,
    validationFailures,
    permissions,
    clearTrialPersonnel,
    loadTrialPersonnel,
    saveTrialPersonnel,
    validateTrialPersonnel,
    createTrialPersonnel,
    clearTrialGroup,
    loadTrialGroup,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{ groupId: string }>();
    const { trialId } = useParams<{ trialId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const { trialPersonnelId } = useParams<{ trialPersonnelId: string }>();
    const groupIdNumber = parseInt(groupId, 10);
    const trialIdNumber = parseInt(trialId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);
    const trialPersonnelIdNumber = parseInt(trialPersonnelId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.TrialPersonnel = {} as Dtos.TrialPersonnel;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialPersonnel }, extraData)
        );
    };


    // Load trial personnel & group
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];
        let breadcrumbSetter = undefined;

        if (trialGroupIdNumber && !trialGroup && !loadingTrialGroup) {
            loadTrialGroup(trialGroupIdNumber);
        }

        if (
            trialPersonnelIdNumber &&
            !trialPersonnel &&
            !loadingTrialPersonnel
        ) {
            breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupPersonnelDetailsBreadcrumbs
                    : GroupTrialPersonnelDetailsBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    groupIdNumber,
                    trialIdNumber,
                    trialGroupIdNumber,
                    trialPersonnelIdNumber
                )
            );
            loadTrialPersonnel(trialPersonnelIdNumber);
        } else if (
            !trialPersonnelIdNumber &&
            !trialPersonnel &&
            !creatingTrialPersonnel
        ) {
            breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupPersonnelCreateBreadcrumbs
                    : GroupTrialPersonnelCreateBreadcrumbs;

            createTrialPersonnel(trialIdNumber, null, groupIdNumber);
            setBreadcrumbs(
                breadcrumbSetter(
                    groupIdNumber,
                    trialIdNumber,
                    trialGroupIdNumber
                )
            );
        } else if (trialPersonnel && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        groupIdNumber,
        trialIdNumber,
        trialGroupIdNumber,
        trialPersonnelIdNumber,
        trialPersonnel,
        setDrawerItems,
        TrialGroupSubMenu,
        GroupTrialSubMenu,
        permissions,
        setBreadcrumbs,
        TrialGroupPersonnelDetailsBreadcrumbs,
        GroupTrialPersonnelDetailsBreadcrumbs,
        TrialGroupPersonnelCreateBreadcrumbs,
        GroupTrialPersonnelCreateBreadcrumbs,
        loadTrialPersonnel,
        createTrialPersonnel,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTrialPersonnel,
        creatingTrialPersonnel,
        createTrialPersonnelSuccess,
        trialGroup,
        loadingTrialGroup,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialPersonnelFailure) {
            navigate(`/error/${loadTrialPersonnelErrorCode}`);
        }
        if (loadTrialGroupFailure) {
            navigate(`/error/${loadTrialGroupErrorCode}`);
        }
        clearTrialPersonnel();
    }, [
        loadTrialPersonnelFailure,
        loadTrialPersonnelErrorCode,
        loadTrialGroupFailure,
        loadTrialGroupErrorCode,
        navigate,
        clearTrialPersonnel,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];
        let breadcrumbSetter = undefined;

        if (trialPersonnelIdNumber && trialGroup) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupPersonnelDetailsBreadcrumbs
                    : GroupTrialPersonnelDetailsBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    groupIdNumber,
                    trialIdNumber,
                    trialGroupIdNumber,
                    trialPersonnelIdNumber,
                    trialGroup?.group?.name,
                    trialGroup?.trial?.studyAcronym,
                    trialPersonnel?.personnel?.displayWithSalutation
                )
            );
        } else if (!trialPersonnelIdNumber && trialGroup) {
            breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupPersonnelCreateBreadcrumbs
                    : GroupTrialPersonnelCreateBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    groupIdNumber,
                    trialIdNumber,
                    trialGroupIdNumber,
                    trialGroup?.group?.name,
                    trialGroup?.trial?.displayName
                )
            );
        }

        if (trialGroup && trialPersonnel) {
            if (loadTrialPersonnelSuccess) {
                let drawerItemSetter =
                    baseRoute === "trials" ? TrialGroupPersonnelSubMenu : GroupTrialPersonnelSubMenu;
    
                setDrawerItems(
                    drawerItemSetter(
                        trialGroup,
                        trialPersonnel,
                        permissions
                    )
                );
            }
            if (createTrialPersonnelSuccess) {
                setDrawerItems([]);
            }
        }
    }, [
        groupIdNumber,
        trialIdNumber,
        trialGroupIdNumber,
        trialPersonnelIdNumber,
        trialGroup,
        location,
        trialPersonnel,
        TrialGroupPersonnelDetailsBreadcrumbs,
        GroupTrialPersonnelDetailsBreadcrumbs,
        setBreadcrumbs,
    ]);

    // Reload trial personnel after saving
    useEffect(() => {
        if (!savingTrialPersonnel && saveTrialPersonnelSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial group personnel saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                const baseRoute = location?.pathname?.split("/")[1];

                const url: string =
                    baseRoute === "trials"
                        ? `/trials/${trialId}/groups/${groupId}/trialgroup/${trialGroupId}/personnel`
                        : `/groups/${groupId}/trials/${trialId}/trialgroup/${trialGroupId}/personnel`;
                navigate(url);
            } else if (!trialPersonnelIdNumber) {
                const baseRoute = location?.pathname?.split("/")[1];

                const url: string =
                    baseRoute === "trials"
                        ? `/trials/${trialId}/groups/${groupId}/trialgroup/${trialGroupId}/personnel/${trialPersonnel.id}/details`
                        : `/groups/${groupId}/trials/${trialId}/trialgroup/${trialGroupId}/personnel/${trialPersonnel.id}/details`;
                navigate(url);
                clearTrialPersonnel();
                clearTrialGroup();
            }
        }
    }, [
        savingTrialPersonnel,
        saveTrialPersonnelSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialPersonnel && saveTrialPersonnelFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialPersonnel, saveTrialPersonnelFailure]);

    // Cleanup effect to clear the trial personnel & group on component unmount
    useEffect(() => {
        return () => {
            clearTrialPersonnel();
            clearTrialGroup();
            resetForm(reduxFormName);
        };
    }, [clearTrialPersonnel, clearTrialGroup, resetForm, reduxFormName]);

    const onCancel = () => {
        const baseRoute = location?.pathname?.split("/")[1];

        const url: string =
            baseRoute === "trials"
                ? `/trials/${trialId}/groups/${groupId}/trialgroup/${trialGroupId}/personnel`
                : `/groups/${groupId}/trials/${trialId}/trialgroup/${trialGroupId}/personnel`;

        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialPersonnel) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialPersonnel = {
                groupLevelTypeId: 2,
                trialId: trialIdNumber,
                groupId: trialGroup?.groupId,
            } as Dtos.TrialPersonnel;

            await saveTrialPersonnel(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialPersonnel = {} as Dtos.TrialPersonnel;

            if (form && hasBeenSubmitted && !validatingTrialPersonnel) {
                await validateTrialPersonnel(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const personnelSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.personnel 
                ? { label: form?.personnel?.displayWithSalutation, value: form?.personnelId } as Dtos.PersonnelOption 
                : null
        );
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TrialPersonnelRoleUpdate,
                Dtos.Permission.TrialPersonnelRoleCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialPersonnel ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            personnelDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Personnel Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row py-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            personnelId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value: form?.personnelId ?? null,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.personnel
                                                        ? [{ label: form.personnel.displayWithSalutation, value: form.personnel.id }]
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    clearable: true,
                                                    loadOptions: personnelSearchLoader,
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-1",
                                                fieldClassName: "col-sm-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            roleDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Role Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            roleId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "TrialPersonnel",
                                                                "roleId",
                                                                lookups
                                                            ),
                                                            form.roleId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-1",
                                                fieldClassName:
                                                    "col-sm-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            dateStarted: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trialPersonnel",
                                        {
                                            dateCeased: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.personnelId,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialPersonnel",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}

                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingTrialPersonnel,
        formState,
        trialGroup,
        generateOptionsFromLookup,
        findLookup,
        lookups,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
        personnelSearchLoader
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialPersonnelRoleView}
            loading={
                loadingTrialGroup ||
                loadingTrialPersonnel ||
                creatingTrialPersonnel ||
                savingTrialPersonnel
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupPersonnelPageProps> => {
    let trialGroup: Dtos.TrialGroup | undefined = !(
        state.trialGroup.formData instanceof Array
    )
        ? state.trialGroup.formData
        : undefined;
    let trialPersonnel: Dtos.TrialPersonnel | undefined = !(
        state.trialPersonnel.formData instanceof Array
    )
        ? state.trialPersonnel.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialPersonnel: trialPersonnel!,
        trialGroup: trialGroup!,
        loadingTrialPersonnel:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Pending,
        loadTrialPersonnelSuccess:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Success,
        loadTrialPersonnelFailure:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Failure,
        loadTrialPersonnelErrorCode: state.trialPersonnel.loadState.errorCode,
        savingTrialPersonnel:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Pending,
        saveTrialPersonnelSuccess:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Success,
        saveTrialPersonnelFailure:
            state.trialPersonnel.saveState &&
            state.trialPersonnel.saveState.status == RequestState.Failure,
        creatingTrialPersonnel:
            state.trialPersonnel.createState &&
            state.trialPersonnel.createState.status == RequestState.Pending,
        createTrialPersonnelSuccess:
            state.trialPersonnel.createState &&
            state.trialPersonnel.createState.status == RequestState.Success,
        validatingTrialPersonnel:
            state.trialPersonnel.validateState &&
            state.trialPersonnel.validateState.status == RequestState.Pending,
        loadingTrialGroup:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Pending,
        loadTrialGroupFailure:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Failure,
        loadTrialGroupErrorCode: state.trialGroup.loadState.errorCode,
        permissions: permissions,
        lookups: state.trialPersonnel.lookups,
        form: state.reduxForms.trialPersonnel,
        formState: state.trialPersonnel.formState,
        formProperties: state.trialPersonnel.formProperties,
        validationFailures: trialPersonnel
            ? state.trialPersonnel.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialGroupPersonnelPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.LoadTrialPersonnelById,
            dispatch
        ),
        clearTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.Clear,
            dispatch
        ),
        createTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.CreateTrialPersonnel,
            dispatch
        ),
        saveTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.SaveTrialPersonnel,
            dispatch
        ),
        validateTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.ValidateTrialPersonnel,
            dispatch
        ),
        loadTrialGroup: bindActionCreators(
            TrialGroupActions.LoadTrialGroupById,
            dispatch
        ),
        clearTrialGroup: bindActionCreators(TrialGroupActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialGroupPersonnelPage);
